import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { EditSharp } from "@mui/icons-material";
import Axios from "axios";
import { BaseUrl } from "../../Environment";
import { companylogo } from "../../assets/images";
import { getSecureItem } from "../../utilsFunctions";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import Sidebar from "../sidebar/sidebar";

const loanTableHeader = {
  srNo: "Sr No",
  name: "Name",
  details: "Details",
  address: "Address",
  actions: "Actions",
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: "center",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const initialForm = {
  branchId: "",
  branchName: "",
  branchDetails: "",
  branchAddress: "",
};

const Branch = () => {
  toast.configure({});
  const companyId = getSecureItem("loggedinUserCompany");
  const token = getSecureItem("token");
  const loggedinUser = getSecureItem("loggedinUser");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(initialForm);

  /**
   * Fetch branches
   */
  const fetchBranches = useCallback(async () => {
    try {
      const { data } = await Axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId },
        headers,
      });

      setBranches(data?.data || []);
    } catch (err) {
      console.error("Fetch Branches Error:", err);
      toast.error("Failed to load branches", {
        position: "top-center",
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, headers]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  /**
   * Form Change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Edit Branch
   */
  const handleEdit = useCallback((branch) => {
    setForm({
      branchId: branch.branchId,
      branchName: branch.branchName || "",
      branchDetails: branch.branchDetails || "",
      branchAddress: branch.branchAddress || "",
    });

    setOpenModal(true);
  }, []);

  /**
   * Reset Form
   */
  const resetForm = () => setForm(initialForm);

  /**
   * Save Branch
   */
  const handleSave = async () => {
    if (!form.branchName.trim()) {
      toast.error("Branch Name is required", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    try {
      const api = form.branchId
        ? Axios.put(
            `${BaseUrl}/branch/updateBranch`,
            {
              ...form,
              companyId,
            },
            { headers },
          )
        : Axios.post(
            `${BaseUrl}/branch/addBranch`,
            {
              ...form,
              companyId,
            },
            { headers },
          );

      const { data } = await api;

      if (data?.status) {
        toast.success(data.msg, { position: "top-center", autoClose: 2000 });
        setOpenModal(false);
        resetForm();
        fetchBranches();
      }
    } catch (error) {
      console.error(error);
      toast.error("Error saving branch", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  };

  return (
    <>
      <Sidebar />

      <div style={{ marginLeft: 73, height: "100vh" }}>
        {loading ? (
          <Grid container justifyContent="center" mt={2}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container direction="column">
            {/* Header */}
            <Grid item sx={{ backgroundColor: "#fff" }}>
              <Box m={1}>
                <img src={companylogo} width={200} height={40} alt="logo" />
              </Box>
            </Grid>

            {/* Title */}
            <Box
              sx={{
                px: 4,
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Branches</Typography>

              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                  setOpenModal(true);
                }}
                disabled={(loggedinUser?.userType === "superAdmin" && loggedinUser?.company?.planId == 3) ? false : true}
              >
                Add Branch
              </Button>
            </Box>

            {/* Table */}
            <Grid item container px={4} py={2}>
              {(loggedinUser?.userType == "superAdmin" && loggedinUser?.company?.planId == 3) ? <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {Object.values(loanTableHeader).map((header) => (
                        <StyledTableCell key={header}>{header}</StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {branches.map((branch, index) => (
                      <StyledTableRow key={branch.branchId}>
                        <StyledTableCell>{index + 1}</StyledTableCell>
                        <StyledTableCell>{branch.branchName}</StyledTableCell>
                        <StyledTableCell>
                          {branch.branchDetails}
                        </StyledTableCell>
                        <StyledTableCell>
                          {branch.branchAddress}
                        </StyledTableCell>
                        <StyledTableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(branch)}
                          >
                            <EditSharp />
                          </IconButton>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer> : <Typography className="Fonts" fontSize={17}>Please buy the <strong>Premium+ plan</strong> to access Branch features.</Typography>}
            </Grid>
          </Grid>
        )}

        {/* Modal */}
        <Dialog
          fullWidth
          maxWidth="sm"
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            resetForm();
          }}
        >
          <DialogContent>
            <Typography className="Fonts" fontWeight={600} fontSize={17}>
              {form.branchId ? "Edit Branch" : "Add Branch"}
            </Typography>

            <Stack spacing={2} mt={2}>
              <Stack spacing={0.5}>
                <Typography className="Fonts" variant="body2">Branch Name*</Typography>
                <TextField
                  name="branchName"
                  placeholder="Enter Branch Name"
                  value={form.branchName}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography className="Fonts" variant="body2">Branch Details</Typography>
                <TextField
                  name="branchDetails"
                  placeholder="Enter Branch Details"
                  value={form.branchDetails}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography className="Fonts" variant="body2">Branch Address</Typography>
                <TextField
                  name="branchAddress"
                  placeholder="Enter Branch Address"
                  value={form.branchAddress}
                  onChange={handleChange}
                  fullWidth
                />
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>

              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default withNamespaces()(Branch);
